import { useCallback } from "react"

import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import type { WorkflowItem } from "@/api/models"

import { Play, TestTube2 } from "@/components/icons"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { usePendingWorkflowStore } from "@/stores/pending-workflow-store"

interface WorkflowRunDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  workflowId?: string
  workflowTitle?: string
  workflow?: WorkflowItem
  isSaved?: boolean
  onSave?: () => Promise<void>
  defaultProjectId?: string
}

export function WorkflowRunDialog({
  open,
  onOpenChange,
  workflowId,
  workflowTitle,
  workflow,
}: WorkflowRunDialogProps) {
  const { t } = useTranslation(undefined, { keyPrefix: "workflowLanding.runDialog" })
  const navigate = useNavigate()
  const setPendingWorkflow = usePendingWorkflowStore((state) => state.setPendingWorkflow)

  const handleClose = useCallback(() => {
    onOpenChange(false)
  }, [onOpenChange])

  const handleTestInEditor = useCallback(() => {
    onOpenChange(false)
    // Navigate to editor with test panel param
    if (workflowId) {
      void navigate(`/flows/${workflowId}?test=true`)
    }
  }, [navigate, onOpenChange, workflowId])

  const handleRunInExplorer = useCallback(() => {
    onOpenChange(false)
    if (workflow) {
      setPendingWorkflow(workflow)
    }
    void navigate("/explorer")
  }, [navigate, onOpenChange, workflow, setPendingWorkflow])

  const displayTitle = workflowTitle || workflow?.title

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Play className="text-primary size-5" />
            {t("runWorkflow", "Run Flow")}
          </DialogTitle>
          <DialogDescription className="pt-2">
            {t("runDescription", "Choose how you want to run this flow.")}
          </DialogDescription>
        </DialogHeader>

        {displayTitle && (
          <div className="bg-muted/50 rounded-lg border p-4">
            <p className="text-sm font-medium">{displayTitle}</p>
          </div>
        )}

        <div className="flex flex-col gap-3">
          <Button
            className="h-auto justify-start gap-3 p-4"
            disabled={!workflowId}
            variant="outline"
            onClick={handleTestInEditor}
          >
            <TestTube2 className="text-success size-5" />
            <div className="flex flex-col items-start text-left">
              <span className="font-medium">{t("testInEditor", "Test in Editor")}</span>
              <span className="text-muted-foreground text-xs">
                {t("testInEditorDescription", "Debug with visual node highlighting")}
              </span>
            </div>
          </Button>

          <Button
            className="h-auto justify-start gap-3 p-4"
            disabled={!workflow && !workflowId}
            variant="outline"
            onClick={handleRunInExplorer}
          >
            <Play className="text-primary size-5" />
            <div className="flex flex-col items-start text-left">
              <span className="font-medium">{t("runInExplorer", "Run in Explorer")}</span>
              <span className="text-muted-foreground text-xs">
                {t("runInExplorerDescription", "Chat interface with full features")}
              </span>
            </div>
          </Button>
        </div>

        <div className="mt-2 flex justify-end">
          <Button variant="ghost" onClick={handleClose}>
            {t("cancel", "Cancel")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
